import { QueryResult, OperationVariables, NetworkStatus, ApolloError, ServerParseError } from '@apollo/client';
import * as React from 'react';

export { NetworkStatus };

export type LoadingWrapperProps<
  TQueryData = unknown,
  TMappedData = unknown,
  TQueryVariables extends OperationVariables = OperationVariables,
> = {
  loadingPlaceholder?:
    | React.ReactNode
    | ((props: { networkStatus: NetworkStatus; loading: boolean }) => React.ReactNode);
  errorPlaceholder?: (error: ApolloError) => React.ReactNode;
  options?: {
    /* defaults to false */
    ignoreErrors?: boolean;
    /* defaults to equivalent of () => loading */
    showLoadingPlaceholder?: (options: { networkStatus: NetworkStatus; loading: boolean }) => boolean;
  };
  mapData(input: { data: Partial<TQueryData> }): TMappedData;
  children?: React.ReactNode | ((props: TMappedData) => React.ReactNode);
  result?: QueryResult<TQueryData, TQueryVariables>;
};

export class Loader<
  TQueryData,
  TMappedData,
  TQueryVariables extends OperationVariables = OperationVariables,
> extends React.Component<LoadingWrapperProps<TQueryData, TMappedData, TQueryVariables>> {
  render() {
    const { result, children, mapData, loadingPlaceholder, errorPlaceholder, options = {} } = this.props;

    if (!result) {
      return null;
    }

    if (
      (options.showLoadingPlaceholder && options.showLoadingPlaceholder(result)) ||
      (!options.showLoadingPlaceholder && result.loading)
    ) {
      return loadingPlaceholder
        ? typeof loadingPlaceholder === 'function'
          ? loadingPlaceholder(result)
          : loadingPlaceholder
        : 'Loading';
    }

    const error = result.error;

    if (error && (!options || !options.ignoreErrors)) {
      if (errorPlaceholder) {
        return errorPlaceholder(error);
      }

      if (error.networkError && Object.prototype.hasOwnProperty.call(error.networkError, 'bodyText')) {
        const parseError = error.networkError as ServerParseError;
        return `${error.message} – Response: (${parseError.statusCode}) ${parseError.bodyText}`;
      }

      return error.message;
    }

    if (!result.data) {
      return null;
    }

    return typeof children === 'function' ? children(mapData({ data: result.data })) : children;
  }
}
